/**
 * The text map renderer (D33's output, D34's shape): {@link MapGraph} → the
 * transcript's ASCII grid.
 *
 * @remarks
 * One of several renderers over one derivation. This is the surface the
 * terminal demo and the in-transcript `MAP` directive use; `renderMapSvg`
 * draws the same graph for browsers. Pure and deterministic — golden replays
 * pin its output like any other transcript text.
 *
 * @packageDocumentation
 */

import type { MapGraph, MapGraphNode } from './map-graph.js';

import { UNKNOWN_LABEL } from './derive-map-graph.js';

/**
 * The narrowest a cell's name column gets. Until D222 it was also the widest,
 * and a longer name was cut to an ellipsis (`THE END OF TH…`) — a word one
 * drawing prints and another does not (content standards §3.11, rule 4). Now
 * every cell is as wide as the graph's longest drawn name, so the text map
 * says every word the plan says; a map of short names keeps this width, so
 * its rows stay terminal-friendly.
 */
const LEAST_CELL_NAME_WIDTH = 14;

/** The legend every render closes with. */
const LEGEND = '[ ] walked   ( ) known   ??? unknown   * you are here';

/**
 * Render one derived map as transcript text.
 *
 * @param graph - The derived map
 * @param title - What the pack calls its map (D189; `resolveMapPresentation` supplies the default) — set in the transcript's capitals
 * @returns The multi-line render (header, grid, annotations, legend)
 */
export const renderMapText = (graph: MapGraph, title: string): string => {
	// D191: a LABELED readout rather than a counted noun. This string wraps the
	// PACK's `title` in a sentence the engine composes, and `engine-runtime`'s
	// whole contract is carrying packs somebody else wrote — so English plural
	// agreement compiled in here is the same defect D183 refused in the
	// pack-facing template system, arriving one layer down. `LOCATIONS WALKED: 1`
	// has no agreement to get wrong in any language, and it is the shape the
	// title screen and `help` already use.
	const header = `${title.toUpperCase()} — LOCATIONS WALKED: ${String(graph.visitedCount)}`;
	const annotationLines = graph.annotations.map(
		(annotation) => `  ${annotation.directionToken}: ${annotation.fromRoomName} -> ${annotation.toLabel}`,
	);

	return [header, '', ...renderGrid(graph), '', ...annotationLines, LEGEND].join('\n').replaceAll(/\n{3,}/gu, '\n\n');
};

/**
 * Render the node grid with connector rows between cells.
 *
 * @param graph - The derived map
 * @returns The grid's lines, top to bottom
 */
const renderGrid = (graph: MapGraph): readonly string[] => {
	const nodesByCoordinate = new Map(graph.nodes.map((node) => [`${String(node.x)},${String(node.y)}`, node]));
	const { maxX, maxY, minX, minY } = graph.bounds;
	const nameWidth = cellNameWidthOf(graph);
	const cellWidth = nameWidth + 4;
	const gapWidth = 4;
	const lines: string[] = [];

	for (let y = minY; y <= maxY; y += 1) {
		let rowText = '';

		for (let x = minX; x <= maxX; x += 1) {
			const node = nodesByCoordinate.get(`${String(x)},${String(y)}`);
			const hasEastEdge = graph.edges.some((edge) => isEdgeBetween(edge, x, y, x + 1, y));

			rowText += node === undefined ? ' '.repeat(cellWidth) : renderCell(node, nameWidth);
			rowText += x < maxX ? (hasEastEdge ? '─'.repeat(gapWidth) : ' '.repeat(gapWidth)) : '';
		}

		lines.push(rowText.trimEnd());

		if (y < maxY) {
			const connectorText = renderConnectorRow(graph, y, cellWidth, gapWidth);

			if (connectorText.trim() !== '') {
				lines.push(connectorText);
			}
		}
	}

	return lines;
};

/**
 * Render the connector row that sits below one grid row: `│` under a cell
 * with a south edge, and — D138, the eight-point compass — `╲` / `╱` in the
 * GAP between two cells for a diagonal edge, `╳` where both cross. A
 * diagonal is the one non-cardinal that is geometrically drawable, so the
 * grid draws it rather than declining (the annotations stay for up/down/in/
 * out, which have no honest cell).
 *
 * @param graph - The derived map
 * @param y - The row above the connectors
 * @param cellWidth - The rendered width of one cell
 * @param gapWidth - The horizontal gap between cells
 * @returns The connector line (may be blank)
 */
const renderConnectorRow = (graph: MapGraph, y: number, cellWidth: number, gapWidth: number): string => {
	const { maxX, minX } = graph.bounds;
	const leftPadding = Math.floor(cellWidth / 2);
	const gapLeftPadding = Math.floor((gapWidth - 1) / 2);
	let connectorText = '';

	for (let x = minX; x <= maxX; x += 1) {
		const hasSouthEdge = graph.edges.some((edge) => isEdgeBetween(edge, x, y, x, y + 1));

		connectorText += hasSouthEdge
			? `${' '.repeat(leftPadding)}│${' '.repeat(cellWidth - leftPadding - 1)}`
			: ' '.repeat(cellWidth);

		if (x < maxX) {
			const hasSoutheastEdge = graph.edges.some((edge) => isEdgeBetween(edge, x, y, x + 1, y + 1));
			const hasSouthwestEdge = graph.edges.some((edge) => isEdgeBetween(edge, x + 1, y, x, y + 1));
			const diagonal = hasSoutheastEdge && hasSouthwestEdge ? '╳' : hasSoutheastEdge ? '╲' : hasSouthwestEdge ? '╱' : ' ';

			connectorText += `${' '.repeat(gapLeftPadding)}${diagonal}${' '.repeat(gapWidth - gapLeftPadding - 1)}`;
		}
	}

	return connectorText.trimEnd();
};

/** Does this edge join the two given cells, in either order? */
const isEdgeBetween = (
	edge: MapGraph['edges'][number],
	firstX: number,
	firstY: number,
	secondX: number,
	secondY: number,
): boolean =>
	(edge.fromX === firstX && edge.fromY === firstY && edge.toX === secondX && edge.toY === secondY) ||
	(edge.fromX === secondX && edge.fromY === secondY && edge.toX === firstX && edge.toY === firstY);

/** What a cell prints for a node: its name, or the unknown placeholder. */
const cellLabelOf = (node: MapGraphNode): string => node.name ?? UNKNOWN_LABEL;

/**
 * The name column's width for one graph: the longest name it draws, and never
 * narrower than {@link LEAST_CELL_NAME_WIDTH}. Unknown rooms carry no name, so
 * a name the player has not reached cannot widen the grid before its time.
 *
 * @param graph - The derived map
 * @returns The width, in characters
 */
const cellNameWidthOf = (graph: MapGraph): number => Math.max(LEAST_CELL_NAME_WIDTH, ...graph.nodes.map((node) => [...cellLabelOf(node)].length));

/** Render one cell: brackets by knowledge tier, `*` for you-are-here, the name padded to the column. */
const renderCell = (node: MapGraphNode, nameWidth: number): string => {
	const marker = node.isActive ? '*' : ' ';
	const paddedLabel = cellLabelOf(node).padEnd(nameWidth, ' ');

	return node.knowledge === 'revealed' ? `(${marker}${paddedLabel} )` : `[${marker}${paddedLabel} ]`;
};
